import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, of } from 'rxjs';
import { BannerCriticality, CaseCreationBlock, DowntimeBanner } from 'src/app/shared/models/downtime-banner.model';

import { DowntimeBannerService } from '../services/downtime-banner.service';

const EMPTY_BANNER: DowntimeBanner = {
  active: false,
  freeText: '',
  criticality: BannerCriticality.INFO,
  caseCreationBlock: CaseCreationBlock.NONE,
};

@Injectable()
export class DowntimeBannerStore {
  private readonly downtimeBannerService = inject(DowntimeBannerService);

  private readonly _downtimeBanner = signal<DowntimeBanner>(EMPTY_BANNER);

  readonly downtimeBanner = this._downtimeBanner.asReadonly();

  readonly isCaseCreationBlocked = computed(() => {
    const { active, caseCreationBlock } = this._downtimeBanner();
    return active && caseCreationBlock === CaseCreationBlock.ALL;
  });

  readonly isPdiBlocked = computed(() => {
    const { active, caseCreationBlock } = this._downtimeBanner();
    return active && (caseCreationBlock === CaseCreationBlock.PDI || caseCreationBlock === CaseCreationBlock.ALL);
  });

  readonly isRSUBlocked = computed(() => {
    const { active, caseCreationBlock } = this._downtimeBanner();
    return active && (caseCreationBlock === CaseCreationBlock.RSU || caseCreationBlock === CaseCreationBlock.ALL);
  });

  readonly downtimeBannerTone = computed<'info' | 'caution' | 'critical'>(() => {
    switch (this._downtimeBanner().criticality) {
      case BannerCriticality.CRITICAL:
        return 'critical';
      case BannerCriticality.WARNING:
        return 'caution';
      case BannerCriticality.INFO:
      default:
        return 'info';
    }
  });

  fetchDowntimeBanner(): void {
    this.downtimeBannerService
      .getDowntimeBanner()
      .pipe(catchError(() => of(EMPTY_BANNER)))
      .subscribe((status) => {
        this._downtimeBanner.set(status);
      });
  }
}
